import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCatalog } from "../app/CatalogContext";
import { modelPath } from "../app/routes";
import { useI18n } from "../i18n/I18nProvider";
import {
  AlertBanner,
  Badge,
  Button,
  Card,
  Chip,
  Loader,
  RefreshIcon,
  Search,
  Tile,
  typographyClass,
} from "../ui-kit";

const ALL = "__all__";

/**
 * Состояние источников данных.
 *
 * Каталог всегда живой — без него портал не стартует, поэтому отдельного
 * индикатора источника моделей больше нет. Остаётся снимок описаний полей:
 * он от каталога не зависит и может подставиться независимо.
 */
export function CatalogAlerts() {
  const { t } = useI18n();
  const { catalog } = useCatalog();

  return (
    <>
      {catalog?.descriptions_source === "snapshot" && (
        <AlertBanner tone="info">{t("catalog.demoDescriptions")}</AlertBanner>
      )}
    </>
  );
}

export function CatalogPage({ alerts = true }: { alerts?: boolean } = {}) {
  const { t, modelDoc } = useI18n();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>(ALL);

  const catalog = useCatalog();

  const items = useMemo(() => {
    if (!catalog.catalog) return [];
    const needle = query.trim().toLowerCase();
    return catalog.catalog.items
      .map((item) => {
        const doc = modelDoc(item.id_global);
        return {
          ...item,
          name: doc.name ?? item.name,
          description: doc.tagline ?? doc.description ?? item.tagline ?? item.description,
        };
      })
      .filter((item) => category === ALL || item.category === category)
      .filter((item) =>
        needle === ""
          ? true
          : [item.name, item.description, item.slug, item.id_global]
              .join(" ")
              .toLowerCase()
              .includes(needle),
      );
  }, [catalog.catalog, query, category, modelDoc]);

  return (
    <>
      {alerts && <CatalogAlerts />}

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "var(--spacing-4)", flexWrap: "wrap" }}>
          <div>
            <h1 className={typographyClass("header-24-semibold")}>{t("catalog.title")}</h1>
            <p className={typographyClass("text-14-medium")} style={{ color: "var(--color-text-secondary-v2)", marginTop: "var(--spacing-2)", maxWidth: "62ch" }}>
              {t("catalog.subtitle")}
            </p>
          </div>
          <Button variant="tertiary" size="small" startIcon={<RefreshIcon />} onClick={catalog.reload}>
            {t("action.refresh")}
          </Button>
        </div>

        <Search
          value={query}
          placeholder={t("catalog.search")}
          onChange={(event) => setQuery(event.target.value)}
          onClear={() => setQuery("")}
          clearLabel={t("action.clear")}
        />

        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--spacing-2)" }}>
          <Chip selected={category === ALL} onClick={() => setCategory(ALL)}>
            {t("catalog.all")}
          </Chip>
          {(catalog.catalog?.categories ?? []).map((item) => (
            <Chip key={item} selected={category === item} onClick={() => setCategory(item)}>
              {item}
            </Chip>
          ))}
        </div>

        {catalog.loading && <Loader label={t("status.loading")} />}
        {catalog.error && <AlertBanner tone="error" title={t("status.error")}>{catalog.error.message}</AlertBanner>}

        {!catalog.loading && !catalog.error && (
          <>
            <span className={typographyClass("caption-12-medium")} style={{ color: "var(--color-text-secondary-v2)" }}>
              {t("catalog.count", { count: items.length })}
            </span>
            {/* min(260px, 100%) — иначе на экране уже 260px колонка выходила за
                край карточки и страница ехала вбок. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(260px, 100%), 1fr))", gap: "var(--spacing-3)" }}>
              {items.map((item) => (
                <Tile
                  key={item.slug}
                  title={item.name}
                  description={item.description}
                  onClick={() => navigate(modelPath(item.slug))}
                  meta={
                    <span style={{ display: "flex", flexWrap: "wrap", gap: "var(--spacing-1-5)", marginTop: "var(--spacing-2)" }}>
                      <Badge tone="grey" size="small">{item.slug}</Badge>
                      {/* Тип результата — справка, а не действие, поэтому плашка нейтральная:
                          фиолетовый в системе означает «на это можно нажать». */}
                      {item.outputs.map((output) => (
                        <Badge key={output} tone="grey" size="small">{output}</Badge>
                      ))}
                      {/* «Нужен файл» — условие запуска, выделяем контуром, а не заливкой. */}
                      {item.needs_asset && (
                        <Badge tone="outline" size="small">{t("catalog.needsAsset")}</Badge>
                      )}
                      {/* Модель ещё работает, поэтому не прячем и не гасим карточку —
                          предупреждаем плашкой: строить на ней новое не стоит. */}
                      {item.deprecated && (
                        <Badge tone="warning" size="small">{t("model.deprecated")}</Badge>
                      )}
                    </span>
                  }
                />
              ))}
            </div>
            {items.length === 0 && (
              <p className={typographyClass("text-14-medium")} style={{ color: "var(--color-text-secondary-v2)" }}>
                {t("catalog.empty")}
              </p>
            )}
          </>
        )}
      </Card>
    </>
  );
}
